import {Component, HostListener, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {SERIES_DETAILS} from "src/app/app-routing.module";
import {environment} from "@environment";
import {DOCUMENT} from "@angular/common";
import {AbstractComponent} from "@components";
import {
  Country,
  EventFilter,
  EventItem,
  EventItemType,
  League,
  LeagueFilter,
  LeagueStatus,
  Lookups,
  Message,
  PagedFilter,
  Pages, Paging,
  Sport
} from "@model";
import {LeagueService} from "@services";
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import dayjs from "dayjs";
import {getDateFromNgbDate} from "@utility";

@Component({
  selector: 'app-league-calendar',
  templateUrl: './league-calendar.component.html',
  styleUrls: ['./league-calendar.component.scss']
})
export class LeagueCalendarComponent extends AbstractComponent implements OnInit, OnDestroy, Paging<League> {
  @Input() leaguesCountries: Lookups[] = [];
  @Input() leaguesSports: Sport[] = [];
  @Input() listOfLeagues: League[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages: Message[] | undefined;

  EventItemType = EventItemType;
  name: string = "";
  dateTo: NgbDate | undefined;
  dateFrom: NgbDate | undefined;
  images: string[] = [1, 2, 3].map((n: number): string => `${environment.assetsImagesPath}/assets/img/carousel/carousel${n}.png`);
  environment: any = environment;
  showFabButton: boolean = false;
  sportId: number | undefined;
  countryId: number | undefined;
  queryParamsFirstChange: boolean = true;
  isLoading: boolean = false;

  constructor(private router: Router, private leagueService: LeagueService, private route: ActivatedRoute,
              @Inject(DOCUMENT) private document: Document) {
    super();
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset != 0;
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    this.handleExploreResult(this.listOfLeagues);
  }

  private subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
      distinctUntilChanged()).subscribe((queryParams: any) => {

      let startDate: dayjs.Dayjs = dayjs().add(-1, 'years');
      let endDate: dayjs.Dayjs = dayjs().add(1, 'years');
      let initialStartDate: NgbDate = new NgbDate(startDate.toDate().getFullYear(), startDate.toDate().getMonth() + 1, startDate.toDate().getDate());
      let initialEndDate: NgbDate = new NgbDate(endDate.toDate().getFullYear(), endDate.toDate().getMonth() + 1, endDate.toDate().getDate());

      this.sportId = this.getSportValue(queryParams);
      this.countryId = this.getCountryValue(queryParams);
      this.name = queryParams.name ? queryParams.name : "";
      this.dateFrom = this.getNgbDateFromString(queryParams.dateFrom) ?? initialStartDate;
      this.dateTo = this.getNgbDateFromString(queryParams.dateTo) ?? initialEndDate;
      if (!this.queryParamsFirstChange && this.isDateValid(queryParams.dateFrom) && this.isDateValid(queryParams.dateTo)
        && this.isSportValid(queryParams.sport) && this.isCountryValid(queryParams.country)) {
        this.onExplore();
      }
      this.queryParamsFirstChange = false;
    }))
  }

  private changeQueryParams(): void {
    let queryParams: { [key: string]: any } = {};
    if (this.dateFrom) {
      queryParams['dateFrom'] = this.dateFrom.year + "-" + this.dateFrom.month + "-" + this.dateFrom.day
    }
    if (this.dateTo) {
      queryParams['dateTo'] = this.dateTo.year + "-" + this.dateTo.month + "-" + this.dateTo.day
    }
    if (this.sportId) {
      queryParams['sport'] = this.sportId;
    }
    if (this.countryId) {
      queryParams['country'] = this.countryId;
    }
    if (this.name && this.name.length > 0) {
      queryParams['name'] = this.name
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  createFilter(): PagedFilter<LeagueFilter> {
    return new PagedFilter(new Pages(), {
      name: this.name !== "" ? this.name : undefined,
      sportId: this.sportId ?? undefined,
      countries: this.countryId ?? undefined,
      ...this.getLeagueFilterDates()
    });
  }

  getLeagueFilterDates() {
    return {
      startDate: this.dateFrom ? getDateFromNgbDate(this.dateFrom) : undefined,
      endDate: this.dateTo ? getDateFromNgbDate(this.dateTo) : undefined,
    }
  }

  handleExploreResult(listOfLeagues: League[]): void {
    this.setListOfItems(listOfLeagues);
  }

  setListOfItems(listOfLeagues: League[]): void {
    if (listOfLeagues != null && listOfLeagues.length > 0) {
      this.listOfLeagues = listOfLeagues;
    } else {
      this.listOfLeagues = [];
    }
  }

  onExplore() {
    this.isLoading = true;
    this.leagueService.findPublicLeagues(this.createFilter())
      .subscribe((listOfEvents: EventItem[]) => {
          setTimeout(() => {
            this.isLoading = false;
          }, 700);
          this.handleExploreResult(listOfEvents);
        }
      );
  }

  loadMore(): void {
    throw new Error('Method not implemented.');
  }

  isDateValid(dateValue: string | undefined) {
    if (!dateValue) return true;
    else {
      const date: Date = new Date(dateValue);
      return (!isNaN(date.getTime()))
    }
  }

  isSportValid(sportValue: string | undefined) {
    if (!sportValue) return true;
    else {
      return this.leaguesSports.filter((sport: Sport) => sport.id === +sportValue).length > 0 ?? false;
    }
  }

  isCountryValid(countryValue: string | undefined) {
    if (!countryValue) return true;
    else {
      return this.leaguesCountries.filter((country: Country) => country.id === +countryValue).length > 0 ?? false;
    }
  }

  getSportValue(queryParams: any) {
    if (queryParams.sport) {
      return this.isSportValid(queryParams.sport) ? +queryParams.sport : undefined
    }
    return undefined;
  }

  getCountryValue(queryParams: any) {
    if (queryParams.country) {
      return this.isCountryValid(queryParams.country) ? +queryParams.country : undefined
    }
    return undefined;
  }

  getNgbDateFromString(dateString: string | undefined) {
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const ngbDate: NgbDate = new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
        return new NgbDate(ngbDate.year, ngbDate.month, ngbDate.day)
      }
      return undefined;
    }
    return undefined;
  }

  dateChanged(value: { from: NgbDate | undefined, to: NgbDate | undefined }) {
    this.dateFrom = value.from;
    this.dateTo = value.to;
    this.changeQueryParams();
  }

  onSportSelect(sportId?: number) {
    if (this.sportId === sportId || !sportId) {
      this.sportId = undefined;
    } else {
      this.sportId = sportId;
    }
    this.changeQueryParams()
  }

  onCountrySelect(countryId?: number) {
    if (this.countryId === countryId || !countryId) {
      this.countryId = undefined;
    } else {
      this.countryId = countryId;
    }
    this.changeQueryParams()
  }

  handleInput(value: string) {
    this.name = value.trim() ?? "";
    this.changeQueryParams()
  }

  goToSeriesDetailsPage(item: League): void {
    this.router.navigate([SERIES_DETAILS], {
      relativeTo: this.route,
      queryParams: {leagueId: item.id}
    });
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  getActiveLeagues(): League[] {
    if ((this.listOfLeagues.length ?? 0) > 0) {
      return this.listOfLeagues.filter((item: League) => item.status === LeagueStatus.ACTIVE)
    } else {
      return [];
    }
  }

  getFinishedLeagues(): League[] {
    if ((this.listOfLeagues.length ?? 0) > 0) {
      return this.listOfLeagues.filter((item: League) => item.status === LeagueStatus.FINISHED)
    } else {
      return [];
    }
  }

  getCountryName(countryId: number): string {
    return this.leaguesCountries.find((item: Country) => item.id === countryId)?.name ?? "";
  }

  protected readonly League = League;
}
